/** Section 3 — "Partnership Framework with Agencies and 90-Day Activation & Launch Plan" (EN PDF). */
export const partnershipEn = {
  id: 'partnership',
  previewCount: 3,
  navLabel: 'Agency partnership',
  eyebrow: 'Partnership framework',
  title: 'Partnership Framework with Agencies and 90-Day Activation & Launch Plan',
  subtitle:
    'A clear integration of roles: HOLOL leads the vision and the economic and investment agenda, while the agency leads local access and media and commercial execution.',
  blocks: [
    {
      icon: 'campaign',
      title: 'Introduction',
      wide: true,
      paragraphs: [
        "The HOLOL Global Economic Media Initiative is an international economic and investment media project that aims to build an interconnected media network across countries and markets, using media as a tool to present countries' visions, promote their economies, open their markets, and connect them with international investors, companies, institutions, and opportunities.",
        "The initiative is based on a different vision of the role of economic media. Media here is not limited to reporting news or presenting projects; rather, it becomes a means of understanding countries' directions, needs, and opportunities, building dialogue with the parties capable of participating in them, and opening new paths for economic and investment relationships.",
        'The initiative is not limited to a single television program. The program represents one of the media products of the project, alongside digital content, dialogues, meetings, events, and other media tools that serve its objectives.',
        "Accordingly, partnerships with media agencies in different markets are important, as they serve as local partners with market knowledge, media and institutional relationships, and execution capabilities, while HOLOL leads the project's economic and investment vision and agenda.",
      ],
    },
    {
      icon: 'join_inner',
      title: 'Nature and Purpose of the Partnership',
      paragraphs: [
        'The partnership is based on complementarity between the capabilities and expertise of both parties, with the objective of launching and activating the initiative within the local market in a way that creates genuine media, economic, and investment value.',
      ],
      listLabel: 'The relationship is based on the following:',
      bullets: [
        "HOLOL leads the overall vision of the project and its economic and investment agenda.",
        'The agency serves as the local media, execution, and commercial partner.',
        "Both parties work jointly to reach the entities, individuals, and opportunities that serve the project's objectives.",
        'Content, programs, and meetings are developed according to the economic and investment objectives identified for the market.',
      ],
      closing:
        'The objective is not simply to produce media content, but to build a media project capable of opening dialogue, relationships, and access to economic and investment opportunities.',
    },
    {
      icon: 'workspace_premium',
      title: "HOLOL's Role in the Project",
      paragraphs: [
        'HOLOL assumes strategic responsibility for the initiative as the owner of the project, its brand, and its model. Its role includes:',
      ],
      bullets: [
        'Establishing the overall vision and strategic objectives of the project.',
        'Managing the economic and investment agenda of the content.',
        "Understanding the country's vision, priorities, needs, and opportunities in coordination with the relevant entities.",
        'Identifying priority economic and investment sectors and areas.',
        "Identifying the types of individuals and entities that should be targeted to achieve the project's objectives.",
        'Managing strategic relationships related to the project.',
        'Connecting the local market with markets, investors, companies, and institutions from other countries.',
        'Developing the relationships and opportunities that emerge through the content and meetings.',
        "Maintaining the project's identity, objectives, and standards across different markets.",
      ],
      closing:
        "HOLOL's role is therefore not limited to producing content. It is responsible for managing the economic and investment dimension of the project — determining what we seek to achieve, with whom, why, and how the relationships created through media can be transformed into real opportunities.",
    },
    {
      icon: 'groups',
      title: 'Role of the Partner Agency',
      paragraphs: [
        'The agency serves as the local partner that contributes its expertise, relationships, and media and commercial capabilities to activate the project within the market. Its role includes:',
      ],
      bullets: [
        'Facilitating access to relevant government, economic, investment, and media entities.',
        'Assisting in arranging meetings with targeted individuals and entities.',
        'Providing knowledge of the local market, its audience, and its media channels.',
        'Executing production, filming, directing, and technical preparations.',
        'Coordinating with local channels, platforms, and media outlets.',
        'Developing sponsorships, advertising, and commercial partnerships within the market.',
        'Implementing media and promotional campaigns.',
        'Managing local media publishing and distribution.',
        'Contributing to organizing meetings and events related to the project.',
        'Managing local execution in continuous coordination with the HOLOL team.',
      ],
      closing:
        "The agency is an essential partner in the success of the project within the market, while maintaining a clear integration between its local execution responsibilities and HOLOL's strategic role.",
    },
    {
      icon: 'person_search',
      title: 'Content Management and Guest Selection',
      wide: true,
      paragraphs: [
        'Content is managed according to the economic and investment objectives that the project seeks to achieve in each market.',
        'HOLOL leads the strategic agenda and determines the priorities in terms of themes, objectives, entities, and individuals, while the agency provides its local perspective regarding the best means of access and media execution.',
        "Guests and participating entities are selected not merely for media exposure, but according to the value their participation can add to the project's objectives — whether through knowledge, relationships, investment, partnerships, or opening new markets.",
      ],
      closing:
        'Accordingly, media content becomes part of a broader economic and investment project rather than an objective in itself.',
    },
    {
      type: 'phases',
      icon: 'event_upcoming',
      title: '90-Day Activation and Launch Plan',
      outcomeLabel: 'Targeted outcome',
      items: [
        {
          tag: 'Phase One — First 30 days',
          title: 'Establishment and Agenda Development',
          bullets: [
            'Form the joint working team.',
            'Define responsibilities and points of contact.',
            'Begin communication with targeted government, economic, and investment entities.',
            "Understand the market's priorities, needs, and opportunities.",
            'Identify the key sectors and topics.',
            'Prepare an initial list of targeted guests and entities.',
            'Develop the media and execution framework.',
            'Prepare the initial commercial concept for sponsorships and partnerships.',
          ],
          outcome:
            'Approval of the core agenda, work plan, and priority list of entities and individuals.',
        },
        {
          tag: 'Phase Two — Days 31 to 60',
          title: 'Preparation and Activation',
          bullets: [
            'Finalize the main topics and areas of focus.',
            'Begin confirming participation of targeted individuals and entities.',
            'Develop sponsorship and commercial partnership proposals.',
            'Complete media and technical arrangements.',
            'Establish the production and filming schedule.',
            'Prepare promotional materials.',
            'Coordinate with relevant international parties connected to the proposed topics and opportunities.',
          ],
          outcome:
            'Content, guests, commercial partners, and the production and launch plan are ready for execution.',
        },
        {
          tag: 'Phase Three — Days 61 to 90',
          title: 'Production and Launch',
          bullets: [
            'Execute production and filming.',
            'Launch the media and promotional campaign.',
            'Launch the program and content through the agreed channels and platforms.',
            'Activate the digital and media presence.',
            'Organize meetings and dialogues related to the project when required.',
            'Follow up on relationships and opportunities generated through the project.',
            'Evaluate the results of the first phase and establish the plan for the next phase.',
          ],
          outcome:
            'Actual launch of the project within the market, achievement of media and economic presence, and the beginning of developing relationships and opportunities generated by the project.',
        },
      ],
    },
    {
      icon: 'payments',
      title: 'Commercial Model',
      paragraphs: [
        'The cooperation is based on the principle that the value created by each party should be reflected in the commercial model of the partnership.',
        'The appropriate financial model is agreed upon according to the nature of the market, the level of contribution of each party, and its actual responsibilities.',
      ],
      listLabel: 'Revenue areas may include:',
      bullets: [
        'Sponsorships.',
        'Advertising and commercial partnerships.',
        'Media services and products.',
        'Economic events and meetings.',
        'Opportunities and projects that emerge from the relationships the project contributes to building and developing.',
      ],
      closing:
        'The financial details and revenue-sharing mechanisms will be determined as part of the final commercial and legal agreement between the parties.',
    },
    {
      icon: 'history_edu',
      title: 'Next Steps and Legal Framework',
      paragraphs: [
        'Following preliminary approval of the cooperation framework, the process will move to:',
      ],
      numbered: [
        'Forming the joint working team.',
        "Holding a working meeting to determine the market's priorities.",
        'Identifying priority entities, individuals, and areas of focus.',
        'Approving the media, economic, and investment agenda.',
        'Approving the production and launch plan.',
        'Agreeing on the commercial model.',
        'Preparing and signing the legal agreement governing the partnership.',
      ],
      listLabel: 'The final agreement will include, as mutually agreed:',
      bullets: [
        'Responsibilities of each party.',
        'Intellectual property and brand rights.',
        'Confidentiality and information protection.',
        'Commercial model and revenues.',
        'Partnership duration.',
        'Management and decision-making mechanisms.',
        'Mechanisms for expansion, renewal, or termination of the cooperation.',
      ],
      closing:
        'The 90-day period remains a practical proposed framework for activation and launch, and its details may be adjusted according to the nature of the market, the speed of procedures, and the agreement between the parties.',
    },
    {
      icon: 'flag',
      title: 'Conclusion',
      wide: true,
      highlight: true,
      paragraphs: [
        "The initiative does not aim to produce a traditional media program. Rather, it aims to build an economic and investment media project that uses the power of media to present countries' visions, promote their economies, open their markets, and connect them with international investors, companies, institutions, and opportunities.",
        'The partnership is based on a clear integration of roles: HOLOL leads the vision, economic and investment agenda, and strategic relationships, while the agency leads local access, media execution, and commercial execution within the local market.',
        'Through this integration, the project becomes a starting point for building relationships, opening markets, and developing economic and investment opportunities.',
      ],
    },
  ],
};
